import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Tabs from "../../Components/Tabs";
import DataTable from "react-data-table-component";
import { createColumns } from "../../helpers";
import { fetchPartners } from "../../Redux/Partners/PartnersActions";
import { DATATABLE_CUSTOM_STYLES } from "../../constants/datatableStyles";
import { Link } from "react-router-dom";
import { CLIENTS_CATEGORIES } from "../../constants/globals";
import { absoluteUrl } from "../../_helper";
import CustomPagination from "../../Components/CustomPagination";
import PageTitle from "../../Components/UI/PageTitle";
import RowLoader from "../../Components/UI/RowLoader";
import EyeDetailsIcon from "../../Components/Svgs/EyeDetailsIcon";
import DropdownSelect from "../../Components/DropdownSelect";
import Flatpickr from "react-flatpickr";
import { getTabsTotalNumbersForPartner } from "../../apis";

const PartnersList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [filterByClientCategory, setFilterByClientCategory] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [viewTabsTotalNumbers, setViewTabsTotalNumbers] = useState({});

  const [currentTab, setCurrentTab] = useState(
    new URLSearchParams(location.search).get("tab") || "all"
  );
  const [searchTerm, setSearchTerm] = useState(
    new URLSearchParams(location.search).get("search") || ""
  );
  const [currentPage, setCurrentPage] = useState(
    parseInt(new URLSearchParams(location.search).get("page"), 10) || 1
  );
  const [sortKey, setSortKey] = useState(
    new URLSearchParams(location.search).get("sortKey") || ""
  );
  const [sortDirection, setSortDirection] = useState(
    new URLSearchParams(location.search).get("sortDirection") || ""
  );

  const { partnersData, isLoading, pagination } = useSelector(
    (state) => state.Partners
  );

  const TABS_DATA = [
    {
      key: "all",
      label: "All Partners",
      number: viewTabsTotalNumbers.allPartnersCnt,
    },
    {
      key: "pv-active",
      label: "Active",
      number: viewTabsTotalNumbers.activeCnt,
    },
    { key: "direct", label: "Directs", number: viewTabsTotalNumbers.directCnt },
    {
      key: "pending",
      label: "Pending",
      number: viewTabsTotalNumbers.pendingCnt,
    },
    {
      key: "uncompleted",
      label: "Uncompleted",
      number: viewTabsTotalNumbers.uncompletedCnt,
    },
  ];

  // get Total Numbers
  useEffect(() => {
    getTabsTotalNumbersForPartner()
      .then((response) => {
        setViewTabsTotalNumbers(response.data.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  useEffect(() => {
    dispatch(
      fetchPartners(
        currentTab,
        searchTerm,
        currentPage,
        sortKey,
        sortDirection,
        filterByClientCategory,
        startDate,
        endDate
      )
    );
  }, [
    currentTab,
    searchTerm,
    currentPage,
    sortKey,
    sortDirection,
    dispatch,
    filterByClientCategory,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("tab", currentTab);
    if (searchTerm) params.set("search", searchTerm);
    params.set("page", currentPage);
    if (sortKey) params.set("sortKey", sortKey);
    if (sortDirection) params.set("sortDirection", sortDirection);
    navigate({ search: params.toString() });
  }, [currentTab, searchTerm, currentPage, sortKey, sortDirection, navigate]);

  const handleTabClick = (key) => {
    setCurrentTab(key);
    setCurrentPage(1); // Reset to first page on tab change
  };

  const handleFilter = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on search change
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSortChange = (column, direction) => {
    let currentSortKey;
    switch (column.sortKey) {
      case "Name":
        currentSortKey = "name";
        break;
      case "JoinedDate":
        currentSortKey = "joined_date";
        break;
      default:
        currentSortKey = "";
    }
    setSortKey(currentSortKey);
    setSortDirection(direction);
    setCurrentPage(1); // Reset to first page on sort change
  };

  const filterAndSortData = () => {
    return partnersData;
  };

  const hiddenColumns = [
    "id",
    "action",
    "must_verify_email",
    "client_id",
    "can_be_deleted",
  ]; // Add column keys you want to hide

  const customColumns =
    currentTab === "all" ||
    currentTab === "pv-active" ||
    currentTab === "direct"
      ? [
          {
            name: "Partner's Tree",
            selector: (row) => row.name + " - custom",
            ui: (row) => (
              <Link to={`/tree/${row.id}`}>
                <img
                  src={absoluteUrl("imgs/tree.png")}
                  style={{ width: 30 }}
                  alt=""
                  className="icon-color"
                />
              </Link>
            ),
          },
        ]
      : currentTab === "uncompleted"
      ? [
          {
            name: "Action",
            selector: (row) => row.name + " - uncompleted",
            ui: (row) => (
              <div className="d-flex gap-2">
                <Link
                  className="hover:!tw-text-primary tw-whitespace-nowrap"
                  to={
                    row.must_verify_email
                      ? `/partners/update/${row.id}/?verifyEmail=true`
                      : `/partners/edit/${row.id}`
                  }
                >
                  {row.action}
                </Link>
                <div className="">|</div>
                <Link
                  to={`/partners/update/${row.id}`}
                  className="ml-2 hover:!tw-text-primary tw-whitespace-nowrap"
                >
                  Edit Step 1
                </Link>
              </div>
            ),
          },
        ]
      : currentTab === "pending"
      ? [
          {
            name: "Action",
            selector: (row) => row.name + " - pending",
            ui: (row) => (
              <div>
                <Link to={`/partners/edit/${row.id}`}>
                  {row.Step_number ? (
                    `Complete Step ${row.Step_number}`
                  ) : (
                    <EyeDetailsIcon />
                  )}
                </Link>
              </div>
            ),
          },
        ]
      : [];

  const columns = useMemo(() => {
    return createColumns(partnersData, customColumns, hiddenColumns, true).map(
      (col) => ({
        ...col,

        sortable:
          currentTab === "direct"
            ? false
            : ["Name", "Joined Date"].includes(col.name),
        width: col.name === "Action" ? "220px" : "1fr",
      })
    );
  }, [partnersData]);

  return (
    <>
      <PageTitle title="Partners" />
      <div className="bg-white flex flex-col mt-4">
        <Tabs
          options={TABS_DATA}
          onTabClick={handleTabClick}
          currentTab={currentTab}
        />

        <div className="bg-white shadow-lg rounded-lg flex flex-col p-2 gap-2 mb-5">
          <div className="relative">
            <div>
              <input
                type="text"
                onChange={handleFilter}
                value={searchTerm}
                className="border border-blue-300 rounded-lg p-2 mb-2 col-md-12"
                placeholder="Search by name, email and phone"
              />
            </div>
            {currentTab === "all" && (
              <div className="tw-grid tw-gap-4 lg:tw-grid-cols-3 tw-grid-cols-1 tw-items-end tw-mb-4">
                <DropdownSelect
                  options={CLIENTS_CATEGORIES}
                  placeholder={"Partner type"}
                  labelKey="name_en"
                  currentOption={filterByClientCategory}
                  setCurrentOption={setFilterByClientCategory}
                />

                <div className="tw-flex tw-items-center tw-relative">
                  <span className="tw-text-[#0B2787] tw-font-[400] tw-whitespace-nowrap tw-w-28 tw-pe-2">
                    Joined From:
                  </span>
                  <Flatpickr
                    value={startDate}
                    onChange={([date]) => setStartDate(date)}
                    className="border border-gray-300 rounded-md p-2 !tw-bg-[#EFEFEF]"
                  />
                  {startDate && (
                    <button
                      onClick={() => setStartDate(null)}
                      className="tw-p-2 tw-text-[#797777] tw-absolute -tw-end-2 tw-text-lg tw-font-extrabold"
                    >
                      x
                    </button>
                  )}
                </div>
                <div className="tw-flex tw-items-center tw-relative">
                  <span className="tw-text-[#0B2787] tw-font-[400] tw-whitespace-nowrap tw-w-28">
                    Joined To:
                  </span>
                  <Flatpickr
                    value={endDate}
                    onChange={([date]) => setEndDate(date)}
                    className="border border-gray-300 rounded-md p-2 !tw-bg-[#EFEFEF]"
                  />
                  {endDate && (
                    <button
                      onClick={() => setEndDate(null)}
                      className="tw-p-2 tw-text-[#797777] tw-absolute -tw-end-2 tw-text-lg tw-font-extrabold"
                    >
                      x
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          <DataTable
            columns={columns}
            data={filterAndSortData()}
            customStyles={DATATABLE_CUSTOM_STYLES}
            progressPending={isLoading}
            onSort={handleSortChange}
            sortServer
            progressComponent={<RowLoader />}
          />

          <div className="flex justify-center mt-4">
            <CustomPagination
              totalPages={pagination?.last_page}
              currentPage={pagination?.current_page || 1}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default PartnersList;
